import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import Card from '@/components/elements/Card/Card';
import { Button } from '@/components/elements/forms/buttons';
import { LoadingIcon } from '@/components/icons';
import Icon from '@/components/icons/Icon';
import SearchInfoWithPopup from '@/components/modules/pages/serp/SearchInfoWithPopup';
import { getAppliedSorting, SORTING_OPTIONS } from '@/features/searchV2/components/Sorting/Sorting.helpers';
import { SortedBy, SortingManagerOutput } from '@/features/searchV2/components/Sorting/Sorting.types';
import { classnames, isServer } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Fragment } from 'react/jsx-runtime';

export type SortingProps = SortingManagerOutput & { loading: boolean };

const Sorting = (props: SortingProps) => {
  const { isMobileView } = useMobileView();
  const [showOptions, setShowOptions] = useState(false);
  const sortedBy = getAppliedSorting(props.urlSorting, false);

  if (isMobileView) {
    return (
      <Fragment>
        <Button
          onClick={() => props.onShowOptions(true)}
          variant="link"
          rightIcon={<Icon color="fg_link" variant="arrow-up-down" style={{ width: 20, height: 20 }} />}>
          {props.loading ? (
            <LoadingIcon style={{ width: 20, height: 20 }} />
          ) : (
            <span className="text-m flex whitespace-nowrap">{_s(sortedBy.label)}</span>
          )}
        </Button>
        {props.showOptions &&
          !isServer &&
          createPortal(
            <Fragment>
              {/* must beat the map carousel's z-9999 */}
              <div className="bg-scrim-40 fixed inset-0 z-[10000]"></div>
              <Card
                bottom
                closable={false}
                onClickOutside={() => props.onShowOptions(false)}
                className="fixed bottom-0 left-0 right-0 z-[10001] text-left">
                <span className="text-capitalize text-xxs text-fg_secondary pb-md block">{_s('sortBy')}</span>
                <ul className="space-y-sm" role="radiogroup">
                  <SortingOptions
                    sortedBy={sortedBy}
                    onChange={(sortOption) => props.onChangeSorting(sortOption.value)}
                    isMobileView={isMobileView}
                  />
                </ul>
                <SearchInfoWithPopup />
              </Card>
            </Fragment>,
            document.body,
          )}
      </Fragment>
    );
  }

  return (
    <div role="group" className="relative" aria-label={''}>
      <DropdownMenu
        initialState={{ isOpen: showOptions }}
        preventScrollFocus
        onStateChange={(setOpen) => setShowOptions(setOpen)}>
        <DropdownMenu.Trigger asChild>
          <Button
            variant="link"
            rightIcon={<Icon color="fg_link" variant="arrow-up-down" style={{ width: 20, height: 20 }} />}>
            {props.loading ? (
              <LoadingIcon style={{ width: 20, height: 20 }} />
            ) : (
              <span className="text-m flex whitespace-nowrap">{_s(sortedBy.label)}</span>
            )}
          </Button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Content
          className={classnames(
            'p-xs space-y-2xs bg-bg_neutral',
            'border-border_focus -mx-[3px] rounded-md border-[3px]',
          )}>
          <SortingOptions
            sortedBy={sortedBy}
            onChange={(sortOption) => props.onChangeSorting(sortOption.value)}
            isMobileView={isMobileView}
          />
        </DropdownMenu.Content>
      </DropdownMenu>
    </div>
  );
};

const dropdownItemBaseClassnames = 'gap-xs flex items-center h-3xl px-sm rounded-sm';

const SortingOptions = ({
  sortedBy,
  isMobileView,
  onChange,
}: {
  sortedBy: SortedBy;
  isSistaminutenSearch?: boolean;
  isMobileView: boolean;
  onChange: (sorting: SortedBy) => void;
}) => {
  return (
    <>
      {SORTING_OPTIONS.filter((option) => {
        // remove legacy sorting option (remove from SORTING_OPTIONS once fully switched to new smt)
        if (option.value === 'smt_discount') {
          return false;
        }
        return true;
      }).map((item, index) => {
        const isActive = item.value === sortedBy?.value;

        if (isMobileView) {
          return (
            <li
              key={index}
              className="gap-sm flex cursor-pointer flex-row-reverse items-center"
              role="radio"
              aria-checked={isActive}
              onClick={() => onChange(item)}>
              <span className="flex-1">{_s(item.label)}</span>
              {isActive ? (
                <Icon variant="radio-checked" className="flex-shrink-0" />
              ) : (
                <Icon variant="radio-unchecked" className="flex-shrink-0" />
              )}
            </li>
          );
        }

        return (
          <DropdownMenu.Item
            closeOnSelect={false}
            onSelect={() => onChange(item)}
            className={classnames(dropdownItemBaseClassnames)}
            key={index}>
            <span className="flex-1">{_s(item.label)}</span>
            {isActive ? (
              <Icon variant="radio-checked" className="flex-shrink-0" />
            ) : (
              <Icon variant="radio-unchecked" className="flex-shrink-0" />
            )}
          </DropdownMenu.Item>
        );
      })}
    </>
  );
};

export default Sorting;
